import React, { useEffect, useMemo, useState } from "react";
import { Outlet } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import FloatingSocial from "./FloatingSocial";

import heroBg from "../../assets/img/heroBg.svg";

import "../../assets/css/layouts/base.css";
import { publicApiClient } from "../../api/apiClient";

// Base 레이아웃 설명
// - 모든 페이지에서 공통으로 Header, Footer, FloatingSocial 적용
// - Hero > 배경은 고정, 텍스트만 페이지별로 바꿔서 사용하시면 됩니다.

// - title: 배너 큰 제목
// - subtitle : 배너 부제
// - caption: 배너 부제 밑 내용들
// - showHero: 배너 노출 여부 (기본 true)
// - useCouncilIntroHero: 총학생회 소개관리 API에서 배너 부제/캡션 문구를 받아와 사용할지 여부
// - hideHeroText: 배너 배경은 보이되 타이틀/부제/캡션 텍스트를 숨길지 여부

// # 사용 방법 예시
// - 배너 보기에 + 텍스트만 교체
// <Route element={<Base title="총학생회" subtitle="…" caption="…"/>}>
//   <Route path="/" element={<Main />} />
// </Route>

// - 총학생회 소개관리 API 값으로 배너 부제/캡션 사용
// <Route element={<Base title="총학생회" useCouncilIntroHero={true} />}>
//   <Route path="/stdcouncil" element={<Council />} />
// </Route>

// - 배너는 보이지만 텍스트 숨기기
// <Route element={<Base hideHeroText={true} />}>
//   <Route path="/stdcouncil" element={<Council />} />
// </Route>

// - 배너 숨기기
// <Route element={<Base showHero={false} />}>
//   <Route path="/cc" element={<CC />} />
// </Route>

const INTRO_API = "/api/council/intro";

const getDataFromApiResponse = (result) => {
  return result?.data?.data || result?.data || result?.result || result || null;
};

const normalizeTermNumber = (value) => {
  if (!value) return "";
  return String(value).replace(/[^0-9]/g, "");
};

const normalizeCouncilStatus = (introData) => {
  const rawStatus =
    introData?.status ||
    introData?.councilStatus ||
    introData?.councilType ||
    introData?.type ||
    "STUDENT_COUNCIL";

  const status = String(rawStatus).toUpperCase();

  if (
    status.includes("EMERGENCY") ||
    status.includes("EMERGENCY_COMMITTEE") ||
    status.includes("비상") ||
    status.includes("비대위") ||
    status.includes("비상대책")
  ) {
    return "EMERGENCY";
  }

  if (
    status.includes("ACTING") ||
    status.includes("권한") ||
    status.includes("대행")
  ) {
    return "ACTING";
  }

  return "STUDENT_COUNCIL";
};

const getCouncilHeroText = (introData, fallbackText) => {
  if (!introData) {
    return fallbackText;
  }

  const termNumber = normalizeTermNumber(introData.termNumber);
  const status = normalizeCouncilStatus(introData);
  const councilName = introData.name || "";
  const slogan = introData.slogan || "";

  if (status === "EMERGENCY") {
    return {
      title: fallbackText.title,
      subtitle: termNumber
        ? `덕성여자대학교 제${termNumber}대 비상대책위원회`
        : "덕성여자대학교 비상대책위원회",
      caption: slogan ? `“${slogan}”` : "",
    };
  }

  if (status === "ACTING") {
    return {
      title: fallbackText.title,
      subtitle: "덕성여자대학교 총학생회 권한대행",
      caption: slogan ? `“${slogan}”` : "",
    };
  }

  return {
    title: fallbackText.title,
    subtitle:
      termNumber || councilName
        ? `덕성여자대학교 ${termNumber ? `제${termNumber}대 ` : ""}총학생회${
            councilName ? ` ${councilName}` : ""
          }`
        : fallbackText.subtitle,
    caption: slogan ? `“${slogan}”` : fallbackText.caption,
  };
};

export default function Base({
  title = "",
  subtitle = "",
  caption = "",
  showHero = true,
  useCouncilIntroHero = false,
  hideHeroText = false,
  children, // Outlet 대신 직접 children을 내려서 써도 됨
}) {
  const [introData, setIntroData] = useState(null);

  useEffect(() => {
    if (!showHero || !useCouncilIntroHero) return;

    const fetchCouncilIntro = async () => {
      try {
        const result = await publicApiClient(INTRO_API);
        const data = getDataFromApiResponse(result);

        setIntroData(data);
      } catch (error) {
        console.error("총학생회 소개 배너 정보 조회 실패:", error);
        setIntroData(null);
      }
    };

    fetchCouncilIntro();
  }, [showHero, useCouncilIntroHero]);

  const fallbackHeroText = useMemo(
    () => ({
      title,
      subtitle,
      caption,
    }),
    [title, subtitle, caption]
  );

  const heroText = useMemo(() => {
    if (useCouncilIntroHero) {
      return getCouncilHeroText(introData, fallbackHeroText);
    }

    return fallbackHeroText;
  }, [useCouncilIntroHero, introData, fallbackHeroText]);

  const hasHeroText = heroText.title || heroText.subtitle || heroText.caption;

  return (
    <>
      <Header />

      {/* 상단 배너(Hero): 배경 이미지는 고정, 텍스트만 바뀜 */}
      {showHero && (
        <section
          className="hero"
          style={{ backgroundImage: `url(${heroBg})` }}
          aria-label="페이지 상단 배너"
        >
          <div className="hero__overlay" />

          {!hideHeroText && hasHeroText && (
            <div className="hero__container">
              {heroText.title && (
                <h1 className="hero__title">{heroText.title}</h1>
              )}

              {heroText.subtitle && (
                <p className="hero__subtitle">{heroText.subtitle}</p>
              )}

              {heroText.caption && (
                <p className="hero__caption">{heroText.caption}</p>
              )}
            </div>
          )}
        </section>
      )}

      {/* 본문 내용 들어가는 자리 */}
      <main
        id="content"
        className={`base__content ${showHero ? "" : "base__content--noTopPad"}`}
      >
        {children ?? <Outlet />}
      </main>
      <Footer />
      <FloatingSocial />
    </>
  );
}